// src/components/dashboard/DashboardPage.tsx
// Layout per docs/2026-01-25-condo-pairing-system/dashboard-layout.md
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigation } from '../../hooks/useNavigation';
import BackgroundShapes from '../common/BackgroundShapes';
import MatchRequestNotification from '../common/MatchRequestNotification';
import CondoReviewNotification from '../common/CondoReviewNotification';
import QuickInsights from './QuickInsights';
import ContactModal from '../common/ContactModal';
import { userAPI } from '../../services/apiService';
import { condoService } from '../../services/condoService';
import Avatar from '../common/Avatar';

interface DashboardPageProps {
  onNavigate: (page: string) => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate }) => {
  const { user, token, logout } = useAuth();
  const navigation = useNavigation();
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null | undefined>(undefined);
  const [condoCount, setCondoCount] = useState<number | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);

  const profileComplete = (user?.profileCompleteness ?? 0) >= 100;
  const profilePercent = user?.profileCompleteness ?? 0;

  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!user?.id || !token) return;
      try {
        const response = await userAPI.getProfile(user.id, token);
        if (response.success && response.data?.user?.personalInfo?.profilePicture) {
          setProfilePictureUrl(response.data.user.personalInfo.profilePicture);
        } else {
          setProfilePictureUrl(null);
        }
      } catch (err) {
        console.error('Error fetching profile picture:', err);
        setProfilePictureUrl(null);
      }
    };
    fetchProfilePicture();
  }, [user?.id, token]);

  useEffect(() => {
    const fetchCondoCount = async () => {
      try {
        const response = await condoService.listCondos(1, 1);
        if (response.success && response.data?.pagination?.total != null) {
          setCondoCount(response.data.pagination.total);
        }
      } catch (err) {
        console.error('Error fetching condo count:', err);
      }
    };
    fetchCondoCount();
  }, []);

  const handleLogout = () => {
    logout();
    onNavigate('landing');
  };

  const handleViewDiscover = () => {
    try {
      navigation.goToDiscover();
    } catch {
      onNavigate('discover');
    }
  };

  const handleOpenMessages = () => {
    try {
      navigation.goToMessages();
    } catch {
      onNavigate('messages');
    }
  };

  const handleEditProfile = () => {
    try {
      navigation.goToProfile();
    } catch {
      onNavigate('profile');
    }
  };

  const handleDiscoverCondos = () => {
    try {
      navigation.goToCondos();
    } catch {
      onNavigate('condos');
    }
  };

  const handleListCondo = () => {
    window.location.href = '/condos/create';
  };

  const avatarTooltip = profileComplete ? 'View profile' : 'Complete your profile';

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header — per dashboard-layout.md */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Clickable avatar + profile status */}
            <button
              type="button"
              onClick={handleEditProfile}
              className="flex items-center gap-2 p-1 -m-1 rounded-full cursor-pointer
                hover:ring-2 hover:ring-terracotta hover:ring-offset-2
                focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2
                transition-all"
              title={avatarTooltip}
              aria-label={avatarTooltip}
            >
              <div className="relative w-12 h-12 flex-shrink-0">
                {/* Progress ring when < 100% (only when user exists) */}
                {user != null && !profileComplete && (
                  <svg
                    className="absolute inset-0 w-full h-full -rotate-90"
                    viewBox="0 0 48 48"
                    aria-hidden
                  >
                    <circle
                      cx="24"
                      cy="24"
                      r="22"
                      fill="none"
                      stroke="rgb(232, 160, 102)"
                      strokeWidth="3"
                      strokeDasharray={`${2 * Math.PI * 22}`}
                      strokeDashoffset={`${2 * Math.PI * 22 * (1 - profilePercent / 100)}`}
                    />
                  </svg>
                )}
                <Avatar
                  profilePictureUrl={profilePictureUrl}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  userId={user?.id}
                  size="medium"
                  loading={profilePictureUrl === undefined}
                  containerClassName="border-2 border-coffee shadow-sm"
                />
              </div>
              {user != null && !profileComplete && (
                <span className="text-sm font-semibold text-coffee">({profilePercent}%)</span>
              )}
            </button>

            <div>
              <h1 className="text-3xl font-bold text-coffee mb-1">
                {profileComplete ? `Welcome back, ${user?.firstName}!` : `Welcome, ${user?.firstName}!`}
              </h1>
              {!profileComplete && (
                <button
                  type="button"
                  onClick={handleEditProfile}
                  className="text-terracotta hover:text-[#d89050] text-sm font-medium transition-colors"
                >
                  Update Profile →
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <CondoReviewNotification />
            <MatchRequestNotification />
            <button
              onClick={handleLogout}
              className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Row 1: VIEW CONDOS | MESSAGE | DISCOVER USER */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm flex flex-col">
            <h2 className="text-xl font-bold text-coffee mb-4">VIEW LISTINGS</h2>
            <div className="flex flex-col items-center flex-1 justify-between text-center">
              <div>
                <div className="text-3xl font-bold text-coffee mb-2">
                  {condoCount ?? '—'}
                </div>
                <p className="text-coffee opacity-80 text-sm">Active listings in NYC & Jersey City</p>
              </div>
              <button
                onClick={handleDiscoverCondos}
                className="mt-4 bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                Browse Listings
              </button>
            </div>
          </div>

          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm flex flex-col">
            <h2 className="text-xl font-bold text-coffee mb-4">MESSAGES & GROUPS</h2>
            <div className="flex flex-col flex-1 justify-between">
              <p className="text-coffee opacity-80 text-sm">Chat with your matches and see who's in your condo interest groups.</p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleOpenMessages}
                  className="flex-1 bg-white border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm text-sm font-medium"
                >
                  Chats
                </button>
                <button
                  onClick={() => { window.location.href = '/my-condos?tab=interest-groups'; }}
                  className="flex-1 bg-terracotta text-white border-2 border-coffee px-3 py-2 rounded-lg hover:bg-[#d89050] transition-colors shadow-sm text-sm font-medium"
                >
                  Groups
                </button>
              </div>
            </div>
          </div>

          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm flex flex-col">
            <h2 className="text-xl font-bold text-coffee mb-4">DISCOVER USER</h2>
            <div className="flex flex-col items-center flex-1 justify-between text-center">
              <p className="text-coffee opacity-80 text-sm">Browse roommates matched to your location and lifestyle</p>
              <button
                onClick={handleViewDiscover}
                className="mt-4 bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                Find Roommates
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: SUBMIT & MANAGE LISTING (2-col) | CONTACT US (1-col) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {/* Block 1+2 merged: Submit & Manage Listing */}
          <div className="md:col-span-2 bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm flex flex-col">
            <h2 className="text-xl font-bold text-coffee mb-4">SUBMIT & MANAGE LISTING</h2>
            <p className="text-coffee opacity-80 text-sm mb-4 flex-1">
              Submit a new listing for review, or track your published and pending condos.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleListCondo}
                className="flex-1 bg-white text-coffee border-2 border-coffee px-3 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm text-sm font-medium"
              >
                Submit
              </button>
              <button
                onClick={() => { window.location.href = '/my-condos?tab=published'; }}
                className="flex-1 bg-white text-coffee border-2 border-coffee px-3 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm text-sm font-medium"
              >
                Published
              </button>
              <button
                onClick={() => { window.location.href = '/my-condos?tab=pending'; }}
                className="flex-1 bg-white text-coffee border-2 border-coffee px-3 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm text-sm font-medium"
              >
                Pending
              </button>
            </div>
          </div>

          {/* Block 3: Contact Us */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm flex flex-col">
            <h2 className="text-xl font-bold text-coffee mb-4">CONTACT US</h2>
            <p className="text-coffee opacity-80 text-sm mb-4 flex-1">
              Questions, feedback, or ideas? Send us a message directly.
            </p>
            <button
              onClick={() => setShowContactModal(true)}
              className="bg-white text-coffee px-4 py-2 rounded-lg font-semibold hover:bg-cream transition-all border-2 border-coffee shadow-sm"
            >
              Send Message
            </button>
          </div>
        </div>

        {/* Row 3: TIPS & INSIGHTS (full width) */}
        <div className="mt-8">
          <QuickInsights userProfileCompleteness={user?.profileCompleteness} />
        </div>
      </div>

      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        userName={user ? `${user.firstName || ''}${user.lastName ? ' ' + user.lastName : ''}`.trim() : ''}
        userEmail={user?.email || ''}
      />
    </div>
  );
};

export default DashboardPage;
